import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Instagram, Twitter, Facebook, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubscribing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, { email });
      setSubscribeMessage('Thank you for subscribing!');
      setEmail('');
    } catch (error) {
      setSubscribeMessage('Subscription failed. Please try again.');
    }
    setIsSubscribing(false);
    setTimeout(() => setSubscribeMessage(''), 3000);
  };

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Our Events', path: '/events' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    { name: 'Trade Fair Stall Building', path: '/services/stall-building' },
    { name: 'Corporate Events', path: '/services/corporate-events' },
    { name: 'Live Events', path: '/services/live-events' },
    { name: 'Brand Activation', path: '/services/brand-activation' },
  ];

  const socialLinks = [
    { icon: Linkedin, url: '#', label: 'LinkedIn' },
    { icon: Instagram, url: '#', label: 'Instagram' },
    { icon: Twitter, url: '#', label: 'Twitter' },
    { icon: Facebook, url: '#', label: 'Facebook' },
  ];

  return (
    <footer className="bg-navy text-white" data-testid="footer">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="font-heading text-3xl md:text-4xl text-white mb-4">
                Stay Updated with Industry Insights
              </h3>
              <p className="text-white/70 text-lg">
                Subscribe to our newsletter for the latest trends in exhibitions, events, and brand experiences.
              </p>
            </div>
            <div>
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4" data-testid="newsletter-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 px-6 py-4 bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-gold"
                  data-testid="newsletter-email-input"
                />
                <button 
                  type="submit" 
                  className="btn-primary whitespace-nowrap flex items-center justify-center gap-2"
                  disabled={isSubscribing}
                  data-testid="newsletter-submit-button"
                >
                  {isSubscribing ? 'Subscribing...' : 'Subscribe'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
              {subscribeMessage && (
                <p className="mt-3 text-gold text-sm" data-testid="newsletter-message">{subscribeMessage}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_defense-comms/artifacts/auvagmgz_DFAIRS%20LOGO%20-%20NEW%20BLACK.png" 
              alt="DFAIRS Communications" 
              className="h-16 w-auto mb-6 brightness-0 invert"
            />
            <p className="font-heading text-xl text-white mb-2">DFAIRS Communications</p>
            <p className="text-white/70 mb-6 leading-relaxed">
              World-class Exhibition and Corporate Event Management company, simplifying trade fairs and creating memorable brand experiences.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.url}
                  className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-gold hover:text-navy transition-colors"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-xl text-white mb-6">Quick Links</h4>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="footer-link flex items-center gap-2 group">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-xl text-white mb-6">Our Services</h4>
            <ul className="space-y-4">
              {services.map((service) => (
                <li key={service.name}>
                  <Link to={service.path} className="footer-link flex items-center gap-2 group">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-xl text-white mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-white/70">
                <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <span>P.No-32, W.P. Shiva Puri Colony,<br />Autonagar, Rangareddi,<br />Telangana, India</span>
              </li>
              <li className="flex items-center gap-3 text-white/70">
                <Phone className="w-5 h-5 text-gold flex-shrink-0" />
                <a href="tel:+919876543210" className="hover:text-gold transition-colors">
                  +91 98765 43210
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/70">
                <Mail className="w-5 h-5 text-gold flex-shrink-0" />
                <a href="mailto:info@dfairs.com" className="hover:text-gold transition-colors">
                  info@dfairs.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} DFAIRS Communications. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-white/50 hover:text-gold transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-white/50 hover:text-gold transition-colors">
                Terms of Service
              </Link>
              <Link to="/careers" className="text-white/50 hover:text-gold transition-colors">
                Careers
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
